import { useCallback, useEffect, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { MapPin, Map, CalendarClock, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import { usePermission } from '@/hooks/usePermission';
import { PERMISSIONS } from '@features/shared/constants/permissions';
import { referenceDataService } from '../services/referenceDataService';
import { resetReferenceDataCache } from './ReferenceDataCombobox';
import {
  SO_REFERENCE_RECORD_TYPES,
  type CacheHealthDto,
  type SoReferenceRecordType,
} from '../types/salesOrder.types';

// 86d39ekrd — SO-specific master-data sync modal. Manually refreshes the SO
// reference lists (City/Town, State/Province, Schedules) from NetSuite via the
// shared /admin/cache force-sync + health endpoints. These lists also refresh
// hourly via MasterDataPeriodicSyncWorker; this is the manual escape hatch.

function formatLastSynced(iso: string | null): string {
  if (!iso) return 'Never synced';
  const minutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

type ComboSource = 'cityTowns' | 'stateProvinces' | 'schedules';

interface SyncRow {
  recordType: SoReferenceRecordType;
  /** Matching ReferenceDataCombobox source, cleared after a sync so the dropdown refreshes. */
  comboSource: ComboSource;
  icon: LucideIcon;
  label: string;
}

const ROWS: SyncRow[] = [
  { recordType: SO_REFERENCE_RECORD_TYPES.CityTown, comboSource: 'cityTowns', icon: MapPin, label: 'City / Town' },
  { recordType: SO_REFERENCE_RECORD_TYPES.StateProvince, comboSource: 'stateProvinces', icon: Map, label: 'State / Province' },
  { recordType: SO_REFERENCE_RECORD_TYPES.Schedule, comboSource: 'schedules', icon: CalendarClock, label: 'Delivery Schedules' },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SalesOrderReferenceSyncDialog({ open, onOpenChange }: Props) {
  const canSync = usePermission(PERMISSIONS.QUOTATIONS_SYNC_MASTER_DATA);
  const [health, setHealth] = useState<Record<string, CacheHealthDto>>({});
  // Set of in-flight record types — each row syncs independently so the user can
  // fire all three at once; a busy row keeps its button clickable (just spins).
  const [syncing, setSyncing] = useState<Set<SoReferenceRecordType>>(new Set());

  const load = useCallback(async () => {
    try {
      const rows = await referenceDataService.getCacheHealth();
      const byType: Record<string, CacheHealthDto> = {};
      rows.forEach((r) => {
        byType[r.recordType] = r;
      });
      setHealth(byType);
    } catch {
      // non-fatal — the sync buttons still work without the freshness panel
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const runSync = (row: SyncRow) => async () => {
    setSyncing((prev) => new Set(prev).add(row.recordType));
    try {
      await referenceDataService.forceSync(row.recordType);
      // Clear the FE combobox cache so the dropdown shows fresh values without a page reload.
      resetReferenceDataCache(row.comboSource);
      toast.success(`${row.label} synced`);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, `${row.label} sync failed`));
    } finally {
      setSyncing((prev) => {
        const next = new Set(prev);
        next.delete(row.recordType);
        return next;
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Sync Reference Data</DialogTitle>
          <DialogDescription>
            Refresh the Sales Order address + schedule lookup lists from NetSuite. These also
            refresh automatically every hour.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          {ROWS.map((row) => {
            const { recordType, icon: Icon, label } = row;
            const entry = health[recordType];
            const detail = entry
              ? `${entry.rowCount.toLocaleString()} entries · ${formatLastSynced(entry.lastRefreshedAt)}`
              : 'In-memory cache';
            const isBusy = syncing.has(recordType);
            return (
              <div
                key={recordType}
                className="grid grid-cols-12 items-center gap-3 rounded-md border bg-muted/30 px-3 py-2"
              >
                <Icon className="col-span-1 h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="col-span-7 min-w-0">
                  <div className="text-sm font-medium">{label}</div>
                  <div className="truncate text-xs text-muted-foreground">{detail}</div>
                </div>
                <div className="col-span-4 flex items-center justify-end">
                  {canSync && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={runSync(row)}
                      disabled={isBusy}
                      className="shrink-0"
                      title="Re-pulls this list from NetSuite. Use if the values look stale."
                    >
                      <RotateCw className={`h-3.5 w-3.5 mr-1.5 ${isBusy ? 'animate-spin' : ''}`} />
                      {isBusy ? 'Syncing…' : 'Sync'}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
